import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('crm_token');
    if (!token) {
      setLoading(false);
      return;
    }
    API.get('/auth/me')
      .then(r => {
        // GET /api/auth/me returns the user object flat (not wrapped in { user }),
        // unlike POST /api/auth/login which returns { token, user }. Reading
        // r.data.user here was always undefined, so setUser(undefined) silently
        // logged out every fresh page load that relied on an existing session
        // (new tab, reopened browser, deep link) even though the server-side
        // token check succeeded.
        setUser(r.data);
        localStorage.setItem('crm_user', JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    // New device/network — step-up verification required before a session
    // is actually issued. Hand the pending_token back to the caller (Login.js)
    // to render the OTP screen instead of finishing login here.
    if (data.otp_required) return data;
    localStorage.setItem('crm_token', data.token);
    localStorage.setItem('crm_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const verifyLoginOtp = async (pendingToken, code) => {
    const { data } = await API.post('/auth/verify-login-otp', { pending_token: pendingToken, code });
    localStorage.setItem('crm_token', data.token);
    localStorage.setItem('crm_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_entity_id');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyLoginOtp, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
